import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, Heart, Plus, ShoppingCart, Mail, Compass, User, HelpCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useBorrowings } from "@/hooks/useBorrowings";
import { useConversations } from "@/hooks/useMessages";
import { usePlatformNotifications } from "@/hooks/usePlatformNotifications";
import { cn } from "@/lib/utils";
import { AddSetQuickModal } from "@/components/dashboard/AddSetQuickModal";

/**
 * MobileBottomNav — Barra de navegación inferior móvil estilo App (Thumb Zone Ergonomics).
 * Reproduce las 5 pestañas de navegación:
 *   1. Inicio (Feed principal de la comunidad)
 *   2. Añadir (+) (Publicar sets al pool comunitario)
 *   3. Catálogo (Botón central destacado: Búsqueda y solicitud de alquiler de sets nuevos)
 *   4. Alquileres / Préstamos (Gestión de sets prestados y en uso con badge)
 *   5. Mensajes (Bandeja de chats P2P con badge de no leídos) / Entrar
 */
export function MobileBottomNav() {
  const { user, openAuthModal } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Queries para badges de notificación (solo si autenticado)
  const { data: borrowings = [] } = useBorrowings();
  const { data: conversations = [] } = useConversations();

  // Solicitudes recibidas pendientes en sets propios
  const pendingRequestsCount = user
    ? borrowings.filter((b) => b.status === "requested" && b.owner_id === user.id).length
    : 0;

  // Número de usuarios con mensajes sin leer
  const unreadUsersCount = user
    ? conversations.filter((c) => c.unreadCount > 0).length
    : 0;

  // Notificaciones de plataforma no leídas
  const { unreadCount: unreadNotifsCount } = usePlatformNotifications();

  // Badge en la pestaña de mensajes: usuarios con mensajes no leídos + notificaciones de plataforma
  const unreadMessagesBadge = unreadUsersCount + unreadNotifsCount;

  const currentPath = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab");
  const isMobilePreview = searchParams.get("view") === "mobile";
  const dashboardTabUrl = (tab: string) =>
    `/dashboard?tab=${tab}${isMobilePreview ? "&view=mobile" : ""}`;

  // Haptic feedback ligero para smartphones compatibles
  const triggerHaptic = () => {
    if (typeof window !== "undefined" && "vibrate" in navigator) {
      try {
        navigator.vibrate(8);
      } catch {
        // Ignorar si el navegador bloquea la vibración
      }
    }
  };

  const navItems = user
    ? [
        {
          id: "inicio",
          label: "Inicio",
          icon: Home,
          isActive: currentPath === "/" && !location.hash,
          onClick: () => {
            triggerHaptic();
            navigate("/");
          },
        },
        {
          id: "anadir",
          label: "Añadir",
          icon: Plus,
          isActive: isAddModalOpen,
          onClick: () => {
            triggerHaptic();
            setIsAddModalOpen(true);
          },
        },
        {
          id: "catalogo",
          label: "Catálogo",
          icon: Compass,
          isAction: true,
          isActive: currentPath === "/catalogo" || currentPath.startsWith("/set/"),
          onClick: () => {
            triggerHaptic();
            navigate("/catalogo");
          },
        },
        {
          id: "alquileres",
          label: "Alquileres",
          icon: ShoppingCart,
          badge: pendingRequestsCount,
          isActive:
            currentPath === "/dashboard" &&
            (!currentTab ||
              currentTab === "alquileres" ||
              currentTab === "sets" ||
              currentTab === "prestamos"),
          onClick: () => {
            triggerHaptic();
            navigate(dashboardTabUrl("alquileres"));
          },
        },
        {
          id: "mensajes",
          label: "Mensajes",
          icon: Mail,
          badge: unreadMessagesBadge,
          isActive:
            currentPath === "/mensajes" ||
            (currentPath === "/dashboard" && (currentTab === "chat" || currentTab === "mensajes")),
          onClick: () => {
            triggerHaptic();
            navigate(dashboardTabUrl("chat"));
          },
        },
      ]
    : [
        {
          id: "inicio",
          label: "Inicio",
          icon: Home,
          isActive: currentPath === "/" && !location.hash,
          onClick: () => {
            triggerHaptic();
            navigate("/");
          },
        },
        {
          id: "anadir",
          label: "Añadir",
          icon: Plus,
          isActive: false,
          onClick: () => {
            triggerHaptic();
            openAuthModal("login");
          },
        },
        {
          id: "catalogo",
          label: "Catálogo",
          icon: Compass,
          isAction: true,
          isActive: currentPath === "/catalogo" || currentPath.startsWith("/set/"),
          onClick: () => {
            triggerHaptic();
            navigate("/catalogo");
          },
        },
        {
          id: "alquileres",
          label: "Alquileres",
          icon: ShoppingCart,
          isActive: false,
          onClick: () => {
            triggerHaptic();
            openAuthModal("login");
          },
        },
        {
          id: "login",
          label: "Entrar",
          icon: User,
          isActive: false,
          onClick: () => {
            triggerHaptic();
            openAuthModal("login");
          },
        },
      ];

  return (
    <>
      <nav
        role="navigation"
        aria-label="Navegación móvil inferior estilo app"
        className={cn(
          "fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-lg border-t border-border/50 pb-[max(0.6rem,env(safe-area-inset-bottom))] pt-1.5 px-2 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]",
          isMobilePreview ? "max-w-md mx-auto border-x border-border/40" : "md:hidden"
        )}
      >
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = item.isActive;
            const isAction = (item as any).isAction;

            if (isAction) {
              return (
                <motion.button
                  key={item.id}
                  type="button"
                  whileTap={{ scale: 0.92 }}
                  onClick={item.onClick}
                  aria-label={item.label === "Catálogo" ? "Explorar catálogo de sets LEGO" : item.label}
                  aria-current={active ? "page" : undefined}
                  className="relative flex flex-col items-center justify-center flex-1 py-1 px-1 -mt-3.5 group select-none"
                >
                  <div
                    className={cn(
                      "w-11 h-11 rounded-full shadow-md flex items-center justify-center ring-4 ring-background transition-all group-hover:scale-105",
                      active
                        ? "bg-gradient-to-tr from-[#E84E36] to-[#ff6b57] text-white shadow-[#E84E36]/30 shadow-lg scale-105 ring-[#E84E36]/20"
                        : "bg-gradient-to-tr from-primary to-primary/90 text-primary-foreground"
                    )}
                  >
                    <Icon className={cn("h-6 w-6 stroke-[2.4] transition-transform", active && "scale-110")} />
                  </div>
                  <span
                    className={cn(
                      "text-[10px] tracking-tight mt-1 transition-colors",
                      active
                        ? "text-[#E84E36] font-semibold dark:text-primary"
                        : "text-muted-foreground group-hover:text-foreground font-medium"
                    )}
                  >
                    {item.label}
                  </span>
                </motion.button>
              );
            }

            return (
              <motion.button
                key={item.id}
                type="button"
                whileTap={{ scale: 0.92 }}
                onClick={item.onClick}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative flex flex-col items-center justify-center flex-1 py-1 px-1 rounded-xl transition-colors min-h-[48px] select-none",
                  active
                    ? "text-[#E84E36] font-semibold dark:text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <div className="relative flex items-center justify-center">
                  <Icon
                    className={cn(
                      "h-5 w-5 transition-transform",
                      active && "scale-110 stroke-[2.4]"
                    )}
                  />

                  {/* Badge de notificación */}
                  {!!item.badge && item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 bg-[#E84E36] text-white font-bold text-[10px] leading-none px-1.5 py-0.5 rounded-full shadow-sm min-w-[16px] text-center animate-in zoom-in-75">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </div>

                <span className="text-[10px] tracking-tight mt-1 truncate max-w-full">
                  {item.label}
                </span>

                {/* Indicador de pestaña activa */}
                {active && (
                  <motion.div
                    layoutId="bottomNavIndicator"
                    className="absolute -top-1 w-6 h-0.5 rounded-full bg-[#E84E36] dark:bg-primary"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </nav>

      {/* Modal para añadir set rápido al tocar el botón central */}
      <AddSetQuickModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />
    </>
  );
}

export default MobileBottomNav;
